import { ArrowUp, ImagePlus, Loader2, Mic, Square, X } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';

import { type ChatAppearance } from '@apps/web/components/phoebe_chat/chat_appearance';
import { getChatInputAppearanceClasses } from '@apps/web/components/phoebe_chat/chat_input_appearance';
import {
    type DemoCardHint,
    extractNameCandidate,
    matchDemoCard,
    matchGhostCompletion,
    matchNameCatch,
    matchPromptTemplate,
    type NameCatchHint,
    type NameCatchMatch,
    promptSeedText,
    type PromptTemplateHint,
    replaceSentenceWithTemplate,
} from '@apps/web/components/phoebe_chat/composer_hints';
import { MentionDropdown } from '@apps/web/components/phoebe_chat/mention_dropdown';
import { FakeChat, LEARN_TASKS } from '@apps/web/features/learn_phoebe';
import {
    editorPlainText,
    type EditorReference,
    extractEditorReferences,
    replaceEditorContent,
    replaceRangeWithMentionPill,
    serializeEditorContent,
    useMentionDropdown,
} from '@apps/web/hooks/use_mention_dropdown';
import { type ChatComposerDraft } from '@apps/web/lib/chat_composer_draft';
import { EVENTS } from '@apps/web/lib/events';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';
import { cn } from '@apps/web/lib/utils';
import {
    useOrganizationCaregivers,
    useOrganizationClients,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

// A single ranked hint shown above the composer. Only one renders at a time.
type AboveComposerHint =
    | { readonly kind: 'name_catch'; readonly hint: NameCatchHint }
    | { readonly kind: 'demo_card'; readonly hint: DemoCardHint }
    | { readonly kind: 'prompt_template'; readonly hint: PromptTemplateHint };

// Stable dismissal key so a dismissed hint does not immediately reappear.
function aboveHintKey(hint: AboveComposerHint): string {
    switch (hint.kind) {
        case 'name_catch':
            return `name:${hint.hint.candidate.toLowerCase()}`;
        case 'demo_card':
            return `demo:${hint.hint.taskId}`;
        case 'prompt_template':
            return `prompt:${hint.hint.taskId}`;
        default: {
            const exhaustive: never = hint;
            return exhaustive;
        }
    }
}

export type ChatInputHandle = {
    readonly appendText: (text: string) => void;
    readonly replaceDraft: (draft: ChatComposerDraft) => void;
    readonly getSerializedContent: () => string;
    readonly getReferences: () => Array<EditorReference>;
    readonly clear: () => void;
    readonly focus: () => void;
    readonly isEmpty: () => boolean;
    readonly isVisible: () => boolean;
};

export type ChatInputImageAttachment = {
    readonly id: string;
    readonly filename: string | null;
    readonly mimeType: string;
    readonly sizeBytes: number;
    readonly previewUrl: string | null;
    readonly status: 'uploading' | 'ready' | 'failed';
    readonly errorMessage?: string | null;
};

export type ChatInputSubmitTrigger =
    | 'form_submit'
    | 'keyboard_enter'
    | 'keyboard_shift_enter';

export type ChatInputSubmitMetadata = {
    readonly trigger: ChatInputSubmitTrigger;
};

type ChatInputProps = {
    readonly onSubmit: (
        serializedContent: string,
        metadata: ChatInputSubmitMetadata,
    ) => void;
    readonly disabled: boolean;
    readonly isAgentBusy: boolean;
    readonly canSubmitWhileAgentBusy?: boolean;
    readonly submitOnShiftEnter?: boolean;
    readonly isStopping: boolean;
    readonly canStopRun?: boolean;
    readonly onStopRun: () => void;
    readonly placeholder: string;
    readonly organizationId: string | null;
    readonly canRecordAudio: boolean;
    readonly isRecording: boolean;
    readonly isTranscribing: boolean;
    readonly isVoiceInputDisabled: boolean;
    readonly onStartRecording: () => void;
    readonly onStopRecording: () => void;
    readonly onEmptyChange?: (isEmpty: boolean) => void;
    // Fired on each keystroke that leaves non-empty content, to drive the
    // multiplayer "is typing…" presence indicator.
    readonly onTyping?: () => void;
    readonly leadingControl?: React.ReactNode;
    readonly imageAttachments?: ReadonlyArray<ChatInputImageAttachment>;
    readonly onImageFilesSelected?: (files: Array<File>) => void;
    readonly onRemoveImageAttachment?: (attachmentId: string) => void;
    readonly imageUploadDisabled?: boolean;
    // The composer historically only accepted PNG/JPEG/GIF/WEBP. When a
    // consumer wires it up for the v3 coordinator chat we broaden the
    // file-picker allowlist so CSVs, PDFs, and text formats reach the
    // user-uploads endpoint. The prop name stays `image*` to avoid a
    // ripple through every existing consumer.
    readonly acceptedUploadMimeTypes?: string;
    readonly className?: string;
    readonly appearance?: ChatAppearance;
};

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
    function ChatInput(
        {
            onSubmit,
            disabled,
            isAgentBusy,
            canSubmitWhileAgentBusy = false,
            submitOnShiftEnter = false,
            isStopping,
            canStopRun = true,
            onStopRun,
            placeholder,
            organizationId,
            canRecordAudio,
            isRecording,
            isTranscribing,
            isVoiceInputDisabled,
            onStartRecording,
            onStopRecording,
            onEmptyChange,
            onTyping,
            leadingControl = null,
            imageAttachments = [],
            onImageFilesSelected,
            onRemoveImageAttachment,
            imageUploadDisabled = false,
            acceptedUploadMimeTypes,
            className,
            appearance = 'default',
        },
        ref,
    ) {
        const posthog = usePostHog();
        const composerRef = useRef<HTMLDivElement>(null);
        const editorRef = useRef<HTMLDivElement>(null);
        const restoreFocusAfterStopRef = useRef(false);
        const fileInputRef = useRef<HTMLInputElement>(null);
        const [empty, setEmpty] = useState(true);
        // Plain-text draft drives the type-aware ghost completion. Kept
        // separate from the serialized content (which carries [[type:id]]
        // mention tokens) so the ghost matcher sees only what the user typed.
        const [draftPlainText, setDraftPlainText] = useState('');
        const [caretAtEnd, setCaretAtEnd] = useState(false);
        const [dismissedAboveKeys, setDismissedAboveKeys] = useState<
            ReadonlySet<string>
        >(new Set());
        // True when the draft contains a mention pill. Splicing hints stay
        // pill-free because they replace prose; name-catch may still run so a
        // coordinator can mention several people in one draft without typing @
        // for every name.
        const [draftHasReference, setDraftHasReference] = useState(false);
        const imageUploadEnabled = onImageFilesSelected !== undefined;
        const hasSubmittableImageAttachment = imageAttachments.some(
            (attachment) => attachment.status === 'ready',
        );
        const acceptedImageTypes = useMemo(
            () =>
                acceptedUploadMimeTypes ??
                'image/png,image/jpeg,image/gif,image/webp',
            [acceptedUploadMimeTypes],
        );
        const acceptedTypeList = useMemo(
            () =>
                acceptedImageTypes
                    .split(',')
                    .map((entry) => entry.trim().toLowerCase())
                    .filter((entry) => entry.length > 0),
            [acceptedImageTypes],
        );
        const matchesAccepted = useCallback(
            (fileType: string) => {
                const normalized = fileType.toLowerCase();
                return acceptedTypeList.some(
                    (accepted) =>
                        accepted === normalized ||
                        (accepted.endsWith('/*') &&
                            normalized.startsWith(accepted.slice(0, -1))),
                );
            },
            [acceptedTypeList],
        );

        const focusEditorAtEnd = useCallback(() => {
            const editor = editorRef.current;
            if (!editor) {
                return;
            }

            editor.focus();
            const selection = window.getSelection();
            if (!selection) {
                return;
            }

            const range = document.createRange();
            range.selectNodeContents(editor);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        }, []);

        useEffect(() => {
            if (isStopping || !restoreFocusAfterStopRef.current) {
                return;
            }

            restoreFocusAfterStopRef.current = false;
            window.requestAnimationFrame(() => {
                const activeElement = document.activeElement;
                if (
                    activeElement === null ||
                    activeElement === document.body ||
                    composerRef.current?.contains(activeElement)
                ) {
                    focusEditorAtEnd();
                }
            });
        }, [focusEditorAtEnd, isStopping]);

        const onContentChange = useCallback(
            (serialized: string) => {
                const nowEmpty = !serialized.trim();
                setEmpty(nowEmpty);
                onEmptyChange?.(nowEmpty);
                if (!nowEmpty) {
                    onTyping?.();
                }
                // editorPlainText (not raw .textContent) so <br> line breaks
                // survive as \n — the matchers' multi-line and sentence
                // boundaries depend on them.
                setDraftPlainText(
                    editorRef.current ? editorPlainText(editorRef.current) : '',
                );
                setDraftHasReference(serialized.includes('[['));
                setCaretAtEnd(
                    editorRef.current ? isCaretAtEnd(editorRef.current) : false,
                );
            },
            [onEmptyChange, onTyping],
        );

        const {
            isOpen: isMentionOpen,
            query: mentionQuery,
            handleInput,
            handleKeyDown: handleMentionKeyDown,
            insertReference,
            dismiss: dismissMention,
            clearEditor,
            dropdownRef,
        } = useMentionDropdown({
            editorRef,
            onContentChange,
            // Mentions are first-class wherever an organization is in
            // context; the dropdown's search hooks no-op without one.
            enabled: organizationId !== null,
        });

        // Name-catch: resolve a typed-out name (no @) to a record and offer to
        // pill it. The fetch is debounced and only runs while a candidate is
        // present.
        const nameCandidate = useMemo(() => {
            if (disabled || isMentionOpen || organizationId === null) {
                return null;
            }
            const editor = editorRef.current;
            const nameCatchDraft = editor
                ? serializeEditorContent(editor)
                : draftPlainText;
            return extractNameCandidate(nameCatchDraft);
        }, [disabled, isMentionOpen, organizationId, draftPlainText]);
        const debouncedCandidate = useDebouncedValue(nameCandidate ?? '', 300);
        const nameSearchOrganizationId =
            debouncedCandidate.length >= 2 ? organizationId : null;
        const { data: nameCatchCaregivers } = useOrganizationCaregivers(
            nameSearchOrganizationId,
            { reason: 'mention-search' },
            { search: debouncedCandidate || undefined, per_page: 5 },
        );
        const { data: nameCatchClients } = useOrganizationClients(
            nameSearchOrganizationId,
            { reason: 'mention-search' },
        );
        const nameCatchHint = useMemo(() => {
            // Only surface once the debounce has caught up to the live
            // candidate, so the hint doesn't flicker mid-typing.
            if (
                nameCandidate === null ||
                nameCandidate !== debouncedCandidate ||
                debouncedCandidate.length < 2
            ) {
                return null;
            }
            return matchNameCatch(
                debouncedCandidate,
                nameCatchCaregivers ?? [],
                nameCatchClients ?? [],
            );
        }, [
            nameCandidate,
            debouncedCandidate,
            nameCatchCaregivers,
            nameCatchClients,
        ]);

        const ghost = useMemo(() => {
            if (disabled || isMentionOpen || draftHasReference || !caretAtEnd) {
                return null;
            }
            return matchGhostCompletion(draftPlainText);
        }, [
            caretAtEnd,
            disabled,
            draftHasReference,
            draftPlainText,
            isMentionOpen,
        ]);

        // Resolve the single above-composer hint when there is no ghost lead
        // to complete. Ghost takes precedence; among above-hints a name-catch
        // match outranks a demo card, which outranks a ready-made prompt. One
        // hint at a time.
        const aboveHint = useMemo<AboveComposerHint | null>(() => {
            if (disabled || isMentionOpen || ghost !== null) {
                return null;
            }
            const candidates: Array<AboveComposerHint> = [];
            if (nameCatchHint !== null) {
                candidates.push({ kind: 'name_catch', hint: nameCatchHint });
            }
            // Demo/prompt inserts splice the draft's plain text, which would
            // destroy an existing mention pill — only offer them pill-free.
            if (!draftHasReference) {
                const demo = matchDemoCard(draftPlainText);
                if (demo !== null) {
                    candidates.push({ kind: 'demo_card', hint: demo });
                }
                const prompt = matchPromptTemplate(draftPlainText);
                if (prompt !== null) {
                    candidates.push({ kind: 'prompt_template', hint: prompt });
                }
            }
            const next = candidates.find(
                (candidate) => !dismissedAboveKeys.has(aboveHintKey(candidate)),
            );
            return next ?? null;
        }, [
            draftPlainText,
            draftHasReference,
            ghost,
            isMentionOpen,
            disabled,
            nameCatchHint,
            dismissedAboveKeys,
        ]);

        // The hints ship unflagged, so usage has to be answerable from events
        // alone (same contract as Learn Phoebe's analytics). Best-effort:
        // analytics must never break the composer.
        const captureHintEvent = useCallback(
            (eventName: string, properties: Record<string, unknown>) => {
                try {
                    posthog?.capture(eventName, {
                        organization_id: organizationId,
                        ...properties,
                    });
                } catch {
                    // Analytics only — never surface.
                }
            },
            [posthog, organizationId],
        );

        const applyComposerText = useCallback(
            (text: string, matchIndex: number) => {
                const editor = editorRef.current;
                if (!editor) {
                    return;
                }
                // Replace only the sentence containing the trigger (plain text;
                // pill-bearing drafts never offer these hints), keeping the
                // rest of a longer draft intact. Read via editorPlainText so
                // offsets line up with the matcher's <br>-aware draft.
                const next = replaceSentenceWithTemplate(
                    editorPlainText(editor),
                    matchIndex,
                    text,
                );
                editor.textContent = next.text;
                editor.focus();
                const point = textOffsetToPoint(editor, next.caretOffset);
                const selection = window.getSelection();
                if (point !== null && selection !== null) {
                    const range = document.createRange();
                    range.setStart(point.node, point.offset);
                    range.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(range);
                } else {
                    focusEditorAtEnd();
                }
                handleInput();
            },
            [focusEditorAtEnd, handleInput],
        );

        const dismissAboveHint = useCallback(
            (hint: AboveComposerHint) => {
                setDismissedAboveKeys(
                    (previous) => new Set([...previous, aboveHintKey(hint)]),
                );
                captureHintEvent(EVENTS.COMPOSER_HINT_DISMISSED, {
                    kind: hint.kind,
                });
            },
            [captureHintEvent],
        );

        // Replace the typed-out name with a real mention pill.
        const applyNameCatch = useCallback(
            (candidate: string, match: NameCatchMatch) => {
                const editor = editorRef.current;
                if (!editor) {
                    return;
                }
                const fullText = editorPlainText(editor);
                const startIndex = fullText.lastIndexOf(candidate);
                if (startIndex < 0) {
                    return;
                }
                const start = textOffsetToPoint(editor, startIndex);
                const end = textOffsetToPoint(
                    editor,
                    startIndex + candidate.length,
                );
                if (start === null || end === null) {
                    return;
                }
                const range = document.createRange();
                range.setStart(start.node, start.offset);
                range.setEnd(end.node, end.offset);
                // Clicking the hint button steals focus from the editor. Put
                // it back before insertion so replaceRangeWithMentionPill's
                // caret placement survives in browsers and jsdom.
                editor.focus();
                replaceRangeWithMentionPill(
                    range,
                    match.type,
                    match.id,
                    match.displayName,
                );
                captureHintEvent(EVENTS.COMPOSER_HINT_ACCEPTED, {
                    kind: 'name_catch',
                    match_type: match.type,
                });
                handleInput();
            },
            [handleInput, captureHintEvent],
        );

        useImperativeHandle(ref, () => ({
            appendText: (text: string) => {
                // Voice transcripts are plain text; the mention
                // dropdown is intentionally not triggered here.
                const editor = editorRef.current;
                if (!editor) {
                    return;
                }
                const trimmed = text.trim();
                if (!trimmed) {
                    return;
                }

                const currentText = editor.textContent?.trim() ?? '';
                if (currentText) {
                    // Ensure space before appended text
                    const lastChild = editor.lastChild;
                    if (lastChild?.nodeType === Node.TEXT_NODE) {
                        const existing = lastChild.textContent ?? '';
                        if (!/\s$/.test(existing)) {
                            lastChild.textContent = existing + ' ';
                        }
                    } else {
                        editor.appendChild(document.createTextNode(' '));
                    }
                }

                editor.appendChild(document.createTextNode(trimmed));
                setEmpty(false);
                onEmptyChange?.(false);
                // Voice transcripts mutate the draft outside onInput, so feed
                // the ghost matcher here too (it otherwise never fires for
                // voice-composed messages).
                setDraftPlainText(editorPlainText(editor));

                focusEditorAtEnd();
                setCaretAtEnd(true);
            },
            replaceDraft: (draft: ChatComposerDraft) => {
                const editor = editorRef.current;
                if (!editor) {
                    return;
                }
                replaceEditorContent(editor, draft.text, draft.references);
                const serialized = serializeEditorContent(editor);
                const nowEmpty = !serialized.trim();
                setEmpty(nowEmpty);
                onEmptyChange?.(nowEmpty);
                setDraftPlainText(editorPlainText(editor));
                setDraftHasReference(serialized.includes('[['));
                focusEditorAtEnd();
                setCaretAtEnd(true);
            },
            getSerializedContent: () => {
                const editor = editorRef.current;
                if (!editor) {
                    return '';
                }
                return serializeEditorContent(editor);
            },
            getReferences: () => {
                const editor = editorRef.current;
                if (!editor) {
                    return [];
                }
                return extractEditorReferences(editor);
            },
            clear: () => {
                clearEditor();
                setEmpty(true);
                onEmptyChange?.(true);
                setDraftPlainText('');
                setDraftHasReference(false);
                setDismissedAboveKeys(new Set());
                setCaretAtEnd(true);
            },
            focus: () => {
                focusEditorAtEnd();
            },
            isEmpty: () => empty,
            // Whether this composer is part of the visible layout. A
            // mounted-but-hidden chat view presents as display: none, which
            // collapses the box to zero. Measured rather than read off
            // offsetParent, which is also null for a position: fixed element
            // and would report a fixed chat panel as hidden.
            isVisible: () => {
                const editor = editorRef.current;
                if (editor === null) {
                    return false;
                }
                const rect = editor.getBoundingClientRect();
                return rect.width > 0 && rect.height > 0;
            },
        }));

        const handleComposerPointerDown = useCallback(
            (
                event:
                    | React.MouseEvent<HTMLDivElement>
                    | React.PointerEvent<HTMLDivElement>,
            ) => {
                if (disabled) {
                    return;
                }

                const editor = editorRef.current;
                if (!editor) {
                    return;
                }

                const targetNode =
                    event.target instanceof Node ? event.target : null;
                if (targetNode && editor.contains(targetNode)) {
                    return;
                }

                if (
                    event.target instanceof Element &&
                    event.target.closest(
                        'button,a,input,textarea,select,[role="button"],[role="menuitem"],[data-mention-dropdown]',
                    )
                ) {
                    return;
                }

                focusEditorAtEnd();
            },
            [disabled, focusEditorAtEnd],
        );

        function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
            if (disabled) {
                return;
            }

            // Let mention dropdown handle first
            if (handleMentionKeyDown(event)) {
                return;
            }

            // Accept the ghost completion with Tab. The mention dropdown
            // consumes Tab as dismiss above, so reaching here means the picker
            // is closed (and the ghost is only ever set while it is closed).
            if (event.key === 'Tab' && !event.shiftKey && ghost !== null) {
                event.preventDefault();
                const editor = editorRef.current;
                if (!editor) {
                    return;
                }
                focusEditorAtEnd();
                // execCommand keeps the browser undo stack intact (same as
                // paste); inserting the trailing '@' opens the mention picker.
                document.execCommand('insertText', false, ghost.insertion);
                captureHintEvent(EVENTS.COMPOSER_HINT_ACCEPTED, {
                    kind: 'ghost',
                    task_id: ghost.taskId,
                });
                handleInput();
                return;
            }

            // Most chat inputs keep Shift+Enter for multiline editing. Admin
            // Agent opts into Shift+Enter submission while busy so keyboard
            // intent can choose queue vs steer delivery.
            if (
                event.key === 'Enter' &&
                (!event.shiftKey || submitOnShiftEnter) &&
                !event.nativeEvent.isComposing
            ) {
                event.preventDefault();
                const editor = editorRef.current;
                if (!editor) {
                    return;
                }
                const content = serializeEditorContent(editor);
                if (
                    content.trim().length > 0 ||
                    hasSubmittableImageAttachment
                ) {
                    onSubmit(content, {
                        trigger: event.shiftKey
                            ? 'keyboard_shift_enter'
                            : 'keyboard_enter',
                    });
                }
            }
        }

        function handlePaste(event: React.ClipboardEvent<HTMLDivElement>) {
            if (imageUploadEnabled && !disabled && !imageUploadDisabled) {
                const uploadFiles = Array.from(
                    event.clipboardData.files,
                ).filter((file) => matchesAccepted(file.type));
                if (uploadFiles.length > 0) {
                    event.preventDefault();
                    onImageFilesSelected?.(uploadFiles);
                    return;
                }
            }

            event.preventDefault();
            const text = event.clipboardData.getData('text/plain');
            // TODO: execCommand is deprecated but is the only
            // way to insert text into contentEditable while
            // preserving the browser undo stack.
            document.execCommand('insertText', false, text);
            handleInput();
        }

        function handleDrop(event: React.DragEvent<HTMLDivElement>) {
            if (!imageUploadEnabled || disabled || imageUploadDisabled) {
                return;
            }
            const uploadFiles = Array.from(event.dataTransfer.files).filter(
                (file) => matchesAccepted(file.type),
            );
            if (uploadFiles.length === 0) {
                return;
            }
            event.preventDefault();
            onImageFilesSelected?.(uploadFiles);
        }

        function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
            if (!imageUploadEnabled || disabled || imageUploadDisabled) {
                return;
            }
            if (
                Array.from(event.dataTransfer.items).some((item) =>
                    matchesAccepted(item.type),
                )
            ) {
                event.preventDefault();
            }
        }

        const isSubmitEnabled =
            !disabled && (!empty || hasSubmittableImageAttachment);
        const showStopButton = isAgentBusy && canStopRun;
        const showSubmitControls = !isAgentBusy || canSubmitWhileAgentBusy;
        const activeAppearance: ChatAppearance =
            appearance === 'v3-agent' &&
            imageAttachments.length === 0 &&
            aboveHint === null
                ? 'v3-agent'
                : 'default';
        const appearanceClasses = getChatInputAppearanceClasses({
            appearance: activeAppearance,
        });

        return (
            <div
                ref={composerRef}
                className={cn(appearanceClasses.composer, className)}
                data-testid="chat-input-composer"
                onClick={handleComposerPointerDown}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onMouseDown={handleComposerPointerDown}
                onPointerDown={handleComposerPointerDown}
            >
                {imageAttachments.length > 0 ? (
                    <div className="mb-2 flex flex-wrap gap-2">
                        {imageAttachments.map((attachment) => (
                            <div
                                key={attachment.id}
                                className="border-border bg-muted/30 flex max-w-44 items-center gap-2 rounded-md border px-2 py-1.5"
                            >
                                {attachment.previewUrl ? (
                                    <img
                                        src={attachment.previewUrl}
                                        alt=""
                                        className="h-9 w-9 rounded object-cover"
                                    />
                                ) : (
                                    <div className="bg-background flex h-9 w-9 items-center justify-center rounded">
                                        <ImagePlus className="text-muted-foreground h-4 w-4" />
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-xs font-medium">
                                        {attachment.filename ?? 'Image'}
                                    </div>
                                    <div className="text-muted-foreground truncate text-[11px]">
                                        {attachment.status === 'uploading'
                                            ? 'Uploading'
                                            : attachment.status === 'failed'
                                              ? (attachment.errorMessage ??
                                                'Upload failed')
                                              : formatImageSize(
                                                    attachment.sizeBytes,
                                                )}
                                    </div>
                                </div>
                                {onRemoveImageAttachment ? (
                                    <button
                                        type="button"
                                        aria-label="Remove image"
                                        className="text-muted-foreground hover:text-foreground flex h-5 w-5 items-center justify-center rounded"
                                        onClick={() => {
                                            onRemoveImageAttachment(
                                                attachment.id,
                                            );
                                        }}
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                ) : null}
                            </div>
                        ))}
                    </div>
                ) : null}
                {aboveHint !== null && aboveHint.kind === 'name_catch' ? (
                    <div className="border-border bg-muted/40 mb-2 flex flex-col gap-2 rounded-lg border px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground shrink-0 text-[11px] font-medium tracking-wide uppercase">
                                {aboveHint.hint.matches.length === 1
                                    ? 'Did you know'
                                    : 'Which one?'}
                            </span>
                            <span className="text-muted-foreground min-w-0 flex-1 truncate">
                                {aboveHint.hint.matches.length === 1
                                    ? 'Pick the record so Phoebe acts on the right person'
                                    : `${aboveHint.hint.matches.length} matches for “${aboveHint.hint.candidate}”`}
                            </span>
                            <button
                                type="button"
                                aria-label="Dismiss suggestion"
                                className="text-muted-foreground hover:text-foreground shrink-0 cursor-pointer"
                                onClick={() => dismissAboveHint(aboveHint)}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {aboveHint.hint.matches.map((match) => (
                                <button
                                    key={`${match.type}:${match.id}`}
                                    type="button"
                                    className="border-border bg-background hover:bg-accent flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium"
                                    onClick={() =>
                                        applyNameCatch(
                                            aboveHint.hint.candidate,
                                            match,
                                        )
                                    }
                                >
                                    <span>@{match.displayName}</span>
                                    <span className="text-muted-foreground">
                                        {match.type === 'caregiver'
                                            ? 'Caregiver'
                                            : 'Client'}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}
                {aboveHint !== null && aboveHint.kind === 'prompt_template' ? (
                    <div className="border-border bg-muted/40 mb-2 flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm">
                        <span className="text-muted-foreground shrink-0 text-[11px] font-medium tracking-wide uppercase">
                            Try this
                        </span>
                        <span className="min-w-0 flex-1 truncate">
                            {aboveHint.hint.templateText}
                        </span>
                        <button
                            type="button"
                            className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0 cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium"
                            onClick={() => {
                                captureHintEvent(
                                    EVENTS.COMPOSER_HINT_ACCEPTED,
                                    {
                                        kind: 'prompt_template',
                                        task_id: aboveHint.hint.taskId,
                                    },
                                );
                                applyComposerText(
                                    aboveHint.hint.templateText,
                                    aboveHint.hint.matchIndex,
                                );
                            }}
                        >
                            Insert
                        </button>
                        <button
                            type="button"
                            aria-label="Dismiss suggestion"
                            className="text-muted-foreground hover:text-foreground shrink-0 cursor-pointer"
                            onClick={() => dismissAboveHint(aboveHint)}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                ) : null}
                {aboveHint !== null && aboveHint.kind === 'demo_card' ? (
                    <DemoCard
                        taskId={aboveHint.hint.taskId}
                        onRun={(text) => {
                            captureHintEvent(EVENTS.COMPOSER_HINT_ACCEPTED, {
                                kind: 'demo_card',
                                task_id: aboveHint.hint.taskId,
                            });
                            applyComposerText(text, aboveHint.hint.matchIndex);
                        }}
                        onDismiss={() => dismissAboveHint(aboveHint)}
                    />
                ) : null}
                <div
                    data-testid="chat-input-editor-shell"
                    className={cn('relative', appearanceClasses.editorShell)}
                >
                    <MentionDropdown
                        open={isMentionOpen}
                        query={mentionQuery}
                        organizationId={organizationId}
                        onSelect={insertReference}
                        onClose={dismissMention}
                        dropdownRef={dropdownRef}
                    />
                    <div
                        ref={editorRef}
                        contentEditable={!disabled}
                        role="textbox"
                        tabIndex={disabled ? -1 : 0}
                        aria-multiline="true"
                        aria-placeholder={placeholder}
                        suppressContentEditableWarning={true}
                        className={cn(
                            'placeholder:text-muted-foreground max-h-[min(40dvh,10rem)] w-full overflow-y-auto bg-transparent text-base break-words whitespace-pre-wrap outline-none sm:max-h-40 sm:text-sm',
                            appearanceClasses.editor,
                            disabled && 'pointer-events-none opacity-50',
                        )}
                        onInput={handleInput}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                    />
                    {empty && (
                        <div
                            data-testid="chat-input-placeholder"
                            className={cn(
                                'text-muted-foreground pointer-events-none absolute inset-0 text-base sm:text-sm',
                                appearanceClasses.placeholder,
                            )}
                        >
                            {placeholder}
                        </div>
                    )}
                    {ghost !== null ? (
                        // Mirror overlay: an invisible copy of the typed draft
                        // reserves the exact glyph width (same font, wrapping,
                        // and box as the editor) so the grey completion begins
                        // right at the caret — no fragile caret measuring.
                        <div
                            aria-hidden="true"
                            className={cn(
                                'pointer-events-none absolute inset-0 text-base break-words whitespace-pre-wrap sm:text-sm',
                                appearanceClasses.ghost,
                            )}
                        >
                            <span className="invisible">{draftPlainText}</span>
                            <span className="text-muted-foreground/60">
                                {ghost.completion}
                                <span className="border-border text-muted-foreground ml-1.5 rounded border px-1 align-middle text-[10px]">
                                    Tab
                                </span>
                            </span>
                        </div>
                    ) : null}
                </div>
                <div
                    className={cn(
                        'items-center gap-2',
                        appearanceClasses.actionRow,
                    )}
                >
                    <div
                        data-testid="chat-input-leading-control"
                        className={cn(
                            'min-w-0',
                            appearanceClasses.leadingControl,
                        )}
                    >
                        {leadingControl}
                    </div>
                    <div className={appearanceClasses.controlGroup}>
                        <div
                            data-testid="chat-input-leading-actions"
                            className={appearanceClasses.leadingActions}
                        >
                            {imageUploadEnabled ? (
                                <React.Fragment>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept={acceptedImageTypes}
                                        multiple={true}
                                        className="hidden"
                                        onChange={(event) => {
                                            const files = Array.from(
                                                event.target.files ?? [],
                                            );
                                            if (files.length > 0) {
                                                onImageFilesSelected?.(files);
                                            }
                                            event.target.value = '';
                                        }}
                                    />
                                    <button
                                        type="button"
                                        aria-label={
                                            acceptedUploadMimeTypes
                                                ? 'Attach file'
                                                : 'Attach image'
                                        }
                                        className={cn(
                                            'text-foreground/70 hover:text-foreground hover:bg-accent flex size-8 items-center justify-center rounded-full transition-colors',
                                            disabled || imageUploadDisabled
                                                ? 'text-foreground/30 hover:text-foreground/30 cursor-default hover:bg-transparent'
                                                : null,
                                        )}
                                        disabled={
                                            disabled || imageUploadDisabled
                                        }
                                        onClick={() => {
                                            fileInputRef.current?.click();
                                        }}
                                    >
                                        <ImagePlus className="h-3.5 w-3.5" />
                                    </button>
                                </React.Fragment>
                            ) : null}
                            {showSubmitControls && canRecordAudio ? (
                                <button
                                    type="button"
                                    aria-label={
                                        isTranscribing
                                            ? 'Transcribing voice message'
                                            : isRecording
                                              ? 'Stop recording'
                                              : 'Record voice message'
                                    }
                                    onClick={() => {
                                        if (isRecording) {
                                            onStopRecording();
                                            return;
                                        }
                                        onStartRecording();
                                    }}
                                    disabled={
                                        isVoiceInputDisabled && !isRecording
                                    }
                                    className={cn(
                                        'relative flex h-8 items-center justify-center rounded-full transition-colors',
                                        appearanceClasses.voiceButton,
                                        isTranscribing
                                            ? 'px-2.5 text-[11px] font-medium'
                                            : 'w-8',
                                        isVoiceInputDisabled && !isRecording
                                            ? 'text-foreground/30 cursor-default'
                                            : isRecording
                                              ? 'cursor-pointer text-red-600 hover:bg-red-50'
                                              : 'text-foreground/70 hover:text-foreground hover:bg-accent cursor-pointer',
                                    )}
                                >
                                    {isTranscribing ? (
                                        <span className="flex items-center gap-1.5">
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            <span>Transcribing</span>
                                        </span>
                                    ) : (
                                        <React.Fragment>
                                            <Mic className="h-4 w-4" />
                                            {isRecording ? (
                                                <span className="absolute top-1 right-1 h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
                                            ) : null}
                                        </React.Fragment>
                                    )}
                                </button>
                            ) : null}
                        </div>
                        <div
                            data-testid="chat-input-trailing-actions"
                            className={appearanceClasses.trailingActions}
                        >
                            {showStopButton ? (
                                <button
                                    type="button"
                                    aria-label="Stop generating"
                                    className={cn(
                                        'flex size-8 cursor-pointer items-center justify-center rounded-full disabled:cursor-not-allowed',
                                        appearanceClasses.stopButton,
                                        isStopping
                                            ? 'bg-muted text-foreground/70'
                                            : 'bg-foreground text-background',
                                    )}
                                    onClick={() => {
                                        restoreFocusAfterStopRef.current = true;
                                        onStopRun();
                                    }}
                                    disabled={isStopping}
                                >
                                    <Square
                                        className="h-3 w-3"
                                        fill="currentColor"
                                    />
                                </button>
                            ) : isAgentBusy && !showSubmitControls ? (
                                <div className="size-8" />
                            ) : null}
                            {showSubmitControls ? (
                                <button
                                    type="submit"
                                    aria-label="Send message"
                                    disabled={!isSubmitEnabled}
                                    className={cn(
                                        'flex size-8 items-center justify-center rounded-full transition-colors',
                                        appearanceClasses.submitButton(
                                            isSubmitEnabled,
                                        ),
                                    )}
                                >
                                    <ArrowUp
                                        className="h-4 w-4"
                                        strokeWidth={2.5}
                                    />
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

// Inline "here's what I'd do" card: reuses the Learn Phoebe scripted fake-chat
// demo for the matched lesson, with a Run it that seeds the real prompt.
function DemoCard({
    taskId,
    onRun,
    onDismiss,
}: {
    readonly taskId: string;
    readonly onRun: (text: string) => void;
    readonly onDismiss: () => void;
}) {
    const task =
        LEARN_TASKS.find((candidate) => candidate.id === taskId) ?? null;
    if (task === null) {
        return null;
    }
    return (
        <div className="border-border bg-muted/40 mb-2 flex flex-col gap-2 rounded-lg border p-2">
            <FakeChat key={task.id} task={task} className="max-h-72" />
            <div className="flex items-center gap-2 px-1">
                <span className="text-muted-foreground text-xs">
                    Here’s what I’d do
                </span>
                <div className="ml-auto flex items-center gap-2">
                    <button
                        type="button"
                        className="bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium"
                        // Seed up to the first @-slot (never a literal
                        // "[@ pick a …]" placeholder) so the picker opens.
                        onClick={() => onRun(promptSeedText(task.prompt))}
                    >
                        Run it
                    </button>
                    <button
                        type="button"
                        aria-label="Dismiss suggestion"
                        className="text-muted-foreground hover:text-foreground cursor-pointer"
                        onClick={onDismiss}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

// Map a plain-text offset over the editor's visible text to a DOM text point.
// Mention pills count by their display text so name-catch can replace a name
// typed after an existing pill, while offsets inside a pill still fail closed.
function textOffsetToPoint(
    editor: HTMLDivElement,
    targetOffset: number,
): { readonly node: Text; readonly offset: number } | null {
    let consumed = 0;
    for (const node of editor.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
            const length = node.textContent?.length ?? 0;
            if (targetOffset <= consumed + length) {
                return { node: node as Text, offset: targetOffset - consumed };
            }
            consumed += length;
        } else if (node instanceof HTMLElement && node.tagName === 'BR') {
            consumed += 1;
        } else if (node instanceof HTMLElement) {
            const length = node.textContent?.length ?? 0;
            if (targetOffset > consumed && targetOffset < consumed + length) {
                return null;
            }
            consumed += length;
        }
    }
    return null;
}

// The ghost completion only makes sense when the caret sits at the very end of
// the draft. Compares the text before the caret against the full text length,
// which is robust to trailing <br>/wrapper nodes the browser may leave in the
// contentEditable (they contribute no text on either side). Returns false for
// a mid-text caret, a selection, or no selection.
function isCaretAtEnd(editor: HTMLDivElement): boolean {
    const selection = window.getSelection();
    if (
        selection === null ||
        selection.rangeCount === 0 ||
        !selection.isCollapsed
    ) {
        return false;
    }
    const caretRange = selection.getRangeAt(0);
    if (!editor.contains(caretRange.endContainer)) {
        return false;
    }
    const beforeCaret = document.createRange();
    beforeCaret.selectNodeContents(editor);
    beforeCaret.setEnd(caretRange.endContainer, caretRange.endOffset);
    return beforeCaret.toString().length === (editor.textContent ?? '').length;
}

function formatImageSize(sizeBytes: number): string {
    if (sizeBytes < 1024) {
        return `${sizeBytes} B`;
    }
    if (sizeBytes < 1024 * 1024) {
        return `${Math.round(sizeBytes / 1024)} KB`;
    }
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}
